import { apiClient } from './apiClient';

export interface SectionFavorite {
  id: string;
  name: string;
  source_tenant_id: string;
  source_draft_id: string;
  section_instance_id: string;
  semantic_role: string;
  preview_ref: string;
  created_by: string;
  created_at: string;
}

export interface SectionFavoriteRequest {
  name: string;
  source_draft_id: string;
  section_instance_id: string;
  semantic_role: string;
}

export interface SectionFavoritePreview {
  html: string;
  section_instance_id?: string;
  semantic_role?: string;
}

export class SectionFavoriteError extends Error {
  status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.status = status;
    this.name = 'SectionFavoriteError';
  }
}

const detailOf = (err: unknown): SectionFavoriteError => {
  const res = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  ).response;
  const detail = res?.data?.detail;
  if (typeof detail === 'string' && detail) {
    return new SectionFavoriteError(detail, res?.status ?? null);
  }
  const message = (detail as { message?: string } | undefined)?.message;
  if (typeof message === 'string' && message) {
    return new SectionFavoriteError(message, res?.status ?? null);
  }
  if (res?.status === 404) {
    return new SectionFavoriteError('Not found on the server.', 404);
  }
  if (res?.status) {
    return new SectionFavoriteError(
      `The server refused this with status ${res.status}.`,
      res.status,
    );
  }
  return new SectionFavoriteError(
    err instanceof Error ? err.message : String(err),
    null,
  );
};

export const sectionFavoritesService = {
  async list(): Promise<SectionFavorite[]> {
    try {
      const { data } = await apiClient.get('/section-favorites');
      return Array.isArray(data) ? data : (data?.favorites ?? []);
    } catch (err: unknown) {
      throw detailOf(err);
    }
  },

  async get(id: string): Promise<SectionFavorite> {
    try {
      const { data } = await apiClient.get(
        `/section-favorites/${encodeURIComponent(id)}`,
      );
      return data;
    } catch (err: unknown) {
      throw detailOf(err);
    }
  },

  async create(req: SectionFavoriteRequest): Promise<SectionFavorite> {
    let data: SectionFavorite;
    try {
      ({ data } = await apiClient.post('/section-favorites', req));
    } catch (err: unknown) {
      throw detailOf(err);
    }
    if (!data?.id) {
      throw new SectionFavoriteError(
        'The server accepted the request but returned no stored record, so nothing is confirmed saved.',
        null,
      );
    }
    return data;
  },

  /**
   * Unpin. The library is a set of pins on history, so removing one removes
   * the pin and never the draft it points at.
   */
  async remove(id: string): Promise<void> {
    try {
      await apiClient.delete(`/section-favorites/${encodeURIComponent(id)}`);
    } catch (err: unknown) {
      throw detailOf(err);
    }
  },

  /** Rename a pin. The name is the only thing about a favorite a person owns. */
  async rename(id: string, name: string): Promise<SectionFavorite> {
    try {
      const { data } = await apiClient.patch(
        `/section-favorites/${encodeURIComponent(id)}`,
        { name },
      );
      return data;
    } catch (err: unknown) {
      throw detailOf(err);
    }
  },

  async preview(id: string): Promise<SectionFavoritePreview> {
    let data: unknown;
    try {
      ({ data } = await apiClient.get(
        `/section-favorites/${encodeURIComponent(id)}/preview`,
      ));
    } catch (err: unknown) {
      throw detailOf(err);
    }
    if (typeof data === 'string') {
      if (!data.trim()) {
        throw new SectionFavoriteError(
          'The server returned an empty preview source.',
          null,
        );
      }
      return { html: data };
    }
    const html = (data as { html?: unknown } | null)?.html;
    if (typeof html !== 'string' || !html.trim()) {
      throw new SectionFavoriteError(
        'The preview source is missing from the server response.',
        null,
      );
    }
    return { ...(data as SectionFavoritePreview), html };
  },
};
